import { useRef } from "react";
import className from "classnames/bind";
import { Button } from "@components";
import { getExhibitMarkup, selectValue } from "../utils";
import ShareButton from "../ShareButton/ShareButton";
import { useKnockoutLines } from "../useKnockoutLines";
import { useViewSize } from "@/providers/ViewSizeContext";
import type { QuizFields } from "./types";

import styles from "./DigitalExhibitQuiz.module.scss";
import { getPlainText } from "@/core/utils";
const cx = className.bind(styles);

type QuizLandingProps = {
  eyebrow?: QuizFields["eyebrow"];
  title?: QuizFields["title"];
  supportingCopy?: QuizFields["supportingCopy"];
  ctaLabel?: QuizFields["ctaLabel"];
  knockoutEnabled?: QuizFields["knockoutEnabled"];
  knockoutColor?: QuizFields["knockoutColor"];
  onBegin: () => void;
  // Focus target when the user returns to the landing (Back on Q1 / Retake) —
  // the parent moves keyboard focus here so Tab reaches Start next
  headingRef?: React.Ref<HTMLHeadingElement>;
};

export default function QuizLanding({
  eyebrow,
  title,
  supportingCopy,
  ctaLabel,
  knockoutEnabled,
  knockoutColor,
  onBegin,
  headingRef,
}: QuizLandingProps) {
  const knockout = selectValue(knockoutColor);
  const knockoutClass = knockoutEnabled ? cx("dx-knockout", knockout && `dx-knockout--${knockout}`) : undefined;
  const titleRef = useRef<HTMLSpanElement>(null);
  const supportingRef = useRef<HTMLDivElement>(null);
  const knockoutColorValue = (knockout as "white" | "black") || "white";
  const { isDesktop } = useViewSize();
  const { ready: titleReady } = useKnockoutLines(titleRef, {
    enabled: Boolean(knockoutEnabled),
    color: knockoutColorValue,
  });
  const supportingMarkup =
    knockoutEnabled && !isDesktop
      ? getExhibitMarkup(supportingCopy).replace(/<br\s*\/?>/gi, " ")
      : getExhibitMarkup(supportingCopy);
  const { ready: supportingReady } = useKnockoutLines(supportingRef, {
    enabled: Boolean(knockoutEnabled),
    color: knockoutColorValue,
    resplitKey: supportingMarkup,
  });

  return (
    <div className={cx("dx-quiz__landing", { "is-knockout": Boolean(knockoutEnabled) })}>
      {eyebrow && (
        <span className={cx("dx-quiz__eyebrow", "dx-caption", knockoutClass)}>{eyebrow}</span>
      )}
      {title && (
        <h2 ref={headingRef} aria-label={title} tabIndex={-1} className={cx("dx-quiz__title", "dx-display-1")}>
          <span
            ref={titleRef}
            className={knockoutClass}
            style={knockoutEnabled && !titleReady ? { opacity: 0 } : undefined}
          >
            {title}
          </span>
        </h2>
      )}
      {supportingCopy && (
        <p
          // remount on the variant flip so SplitText re-splits a clean node
          key={knockoutEnabled && !isDesktop ? "no-br" : "br"}
          ref={supportingRef}
          aria-label={getPlainText(supportingCopy)}
          className={cx("dx-quiz__supporting", "dx-body", knockoutClass)}
          style={knockoutEnabled && !supportingReady ? { opacity: 0 } : undefined}
          dangerouslySetInnerHTML={{ __html: supportingMarkup }}
        />
      )}
      <div className={cx("dx-quiz__landing-actions")}>
        <Button className={cx("dx-quiz__landing-button")} label={ctaLabel || "Take the Quiz"} onClick={onBegin} type="button" />
        <ShareButton className={cx("dx-quiz__landing-button")} moduleSlug="quiz" />
      </div>
    </div>
  );
}
